import { useRef, useState } from 'react'
import type { VerificationChallenge as Challenge } from '@tenfour/contracts'
import type { VerificationResult } from './VerificationChallenge'
import { verificationApi } from '@/lib/verification'
import { registerPasskey } from '@/lib/passkey'
import { generateQRCode } from '@/lib/qrcode'

type Enrollment = Challenge & { secret: string; otpauthUrl: string }
type Step = 'intro' | 'password' | 'authenticator' | 'recovery' | 'passkey' | 'secured'

export function useAccountSecuritySetup() {
  const [step, setStep] = useState<Step>('intro')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [enrollment, setEnrollment] = useState<Enrollment | null>(null)
  const [managementChallenge, setManagementChallenge] = useState<Challenge | null>(null)
  const [qr, setQr] = useState('')
  const [code, setCode] = useState('')
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([])
  const inFlight = useRef(false)

  async function perform(action: () => Promise<void>) {
    if (inFlight.current) return
    inFlight.current = true
    setBusy(true); setError(''); setNotice('')
    try { await action() }
    catch (err) { setError(err instanceof Error ? err.message : 'Could not finish setup. Please try again.') }
    finally { inFlight.current = false; setBusy(false) }
  }

  function goTo(next: Step) {
    if (inFlight.current) return
    setStep(next); setError(''); setNotice(''); setCode('')
    setEnrollment(null); setQr(''); setManagementChallenge(null)
  }
  function startAuthenticator() { goTo('password') }
  function startPasskey() { goTo('passkey') }
  function back() { goTo('intro') }

  async function enroll(result: VerificationResult) {
    if (!result.managementToken) throw new Error('Please confirm your password again.')
    const next = await verificationApi<Enrollment>('enroll', { managementToken: result.managementToken, method: 'totp' })
    if (!next.secret || !next.otpauthUrl) throw new Error('Could not create your setup key. Please try again.')
    const image = await generateQRCode(next.otpauthUrl)
    setEnrollment(next); setQr(image); setCode(''); setManagementChallenge(null); setStep('authenticator')
  }
  async function confirmPassword(password: string) {
    await perform(async () => {
      const result = await verificationApi<Challenge | VerificationResult>('reauthenticate', { password })
      // Another tab may have enabled a factor since the optional prompt opened.
      if ('twoFactorRequired' in result) setManagementChallenge(result)
      else await enroll(result)
    })
  }
  async function verify() {
    if (!enrollment || code.length !== 6) return
    await perform(async () => {
      const result = await verificationApi<VerificationResult>('verify', { challengeToken: enrollment.challengeToken, code, recovery: false })
      if (!result.enabled) throw new Error('Setup was not confirmed. Please try again.')
      setEnrollment(null); setQr(''); setCode('')
      setRecoveryCodes(result.recoveryCodes ?? [])
      setStep(result.recoveryCodes?.length ? 'recovery' : 'secured')
    })
  }
  async function savePasskey() {
    await perform(async () => {
      try { await registerPasskey(); setStep('secured') }
      catch (err) {
        if (err instanceof Error && err.name === 'NotAllowedError') {
          setNotice('Passkey setup was canceled. You can try again or choose an authenticator app.')
        } else throw err
      }
    })
  }
  async function copy(text: string, confirmation: string) {
    try { await navigator.clipboard.writeText(text); setNotice(confirmation) }
    catch { setNotice('Could not copy automatically. Select the text and copy it manually.') }
  }
  function copyKey() { if (enrollment) void copy(enrollment.secret, 'Setup key copied.') }
  function copyRecoveryCodes() { void copy(recoveryCodes.join('\n'), 'Recovery codes copied.') }

  return { step, busy, error, notice, enrollment, managementChallenge, qr, code, setCode, recoveryCodes,
    startAuthenticator, startPasskey, back, confirmPassword, enroll, verify, savePasskey, copyKey, copyRecoveryCodes }
}
